<i18n src="./translations"></i18n>

<template>
  <div
    v-if="productTabs.length"
    class="pdp-tabs"
    :class="classes"
    :data-preload="[$options.name]"
  >
    <div class="tabs-header-container">
      <button
        v-if="canScrollLeft"
        class="tabs-nav left"
        type="button"
        @click="scrollTabs('left')"
      >
        <Icon name="chevronLeft" width="24" height="24" />
      </button>
      <nav
        ref="tabsHeader"
        class="tabs-header"
        role="tablist"
        aria-orientation="horizontal"
      >
        <a
          v-for="tab in productTabs"
          :key="tab"
          class="tab"
          :class="{ active: tab === currentTab }"
          role="tab"
          :aria-selected="tab === currentTab"
          :aria-controls="tab"
          :data-testid="`tab-${tab}-button`"
          @click="onTabClick(tab)"
          >{{ $t(`pdp-tabs.${tab}`) }}</a
        >
      </nav>
      <button
        v-if="canScrollRight"
        class="tabs-nav right"
        type="button"
        @click="scrollTabs('right')"
      >
        <Icon name="chevronRight" width="24" height="24" />
      </button>
    </div>

    <section
      v-for="(componentName, tabKey) in availableTabs"
      :id="tabKey"
      :key="`tab-${tabKey}`"
      class="pdp-tab-section"
      role="tabpanel"
      :aria-labelledby="`tab-${tabKey}-label`"
    >
      <h3>{{ $t(`pdp-tabs.${tabKey}`) }}</h3>
      <component
        :is="componentName"
        v-if="isTabActivated(tabKey)"
        :tab-name="tabKey"
        :restrictions="restrictions"
        :features="features"
        :car-attributes="carAttributes"
        :is-car-selected="isCarSelected"
        :vehicle-compatibility="vehicleCompatibility"
        :vehicle-name="vehicleName"
        :is-drop-shipment="product.isDropShipment"
        :description="product.description"
        :info-texts="product.infoTexts"
        :product-bundles="productBundles"
        :documents="product.documents.items"
        :is-hazardous-good="product.isHazardousGood"
        :hazardous-good-information="product.hazardousGoodInformation"
        :hazardous-good-info="hazardousGoodInfo"
        :article-type="product.articleType"
        :product-id="product.productId"
        :gpsr="product.gpsrResources"
        :has-car-types="product.hasCarTypes"
        :has-oe-numbers="product.hasOeNumbers"
        :active-tab="currentTab === tabKey"
        :plain-tab-base="true"
        :images="product.images"
        :data-testid="`tab-${tabKey}-content`"
      />
      <hr
        v-if="tabKey !== productTabs[productTabs.length - 1]"
        class="divider"
      />
    </section>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import Icon from 'Components/00-generated/Icon.vue';
import { mapState } from 'vuex';
import { InfoTextsEnum } from '../../models/info-texts.enum';
import PdpDescriptionTab from 'Components/02-molecules/pdp-description-tab/PdpDescriptionTab';

// Direct component imports
import PdpTabCarTypes from 'Components/02-molecules/pdp-tab-car-types/PdpTabCarTypes';
import PdpTabOeNumbers from 'Components/02-molecules/pdp-tab-oe-numbers/PdpTabOeNumbers';
import PdpTabHints from 'Components/02-molecules/pdp-tab-hints/PdpTabHints';
import PdpTabGpsr from 'Components/02-molecules/pdp-tab-gpsr/PdpTabGpsr';
import PdpTabInfoTexts from 'Components/02-molecules/pdp-tab-info-texts/PdpTabInfoTexts';

const TABS = {
  description: PdpDescriptionTab,
  generalInfo: PdpTabInfoTexts,
  techInfo: PdpTabInfoTexts,
  spareParts: PdpTabOeNumbers, // Has Ajax
  vehicleTypes: PdpTabCarTypes, // Has Ajax
  hints: PdpTabHints,
  gpsr: PdpTabGpsr,
};

export default {
  name: 'PdpTabs26',
  components: {
    PdpTabOeNumbers,
    PdpDescriptionTab,
    PdpTabCarTypes,
    PdpTabHints,
    PdpTabInfoTexts,
    PdpTabGpsr,
    Icon,
  },
  mixins: [globalMixin],
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    restrictions: {
      type: Array,
      default: () => [],
    },
    features: {
      type: Array,
      default: () => [],
    },
    carAttributes: {
      type: Array,
      default: () => [],
    },
    isCarSelected: {
      type: Boolean,
      default: false,
    },
    vehicleCompatibility: {
      type: String,
      default: 'UNIVERSAL_PRODUCT',
    },
    vehicleName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      tabs: TABS,
      currentTab: 'description',
      sectionEls: [],
      stickyOffset: 0,
      canScrollLeft: false,
      canScrollRight: false,
      activatedTabs: {},
      sectionsObserver: null,
    };
  },
  computed: {
    ...mapState('core', {
      viewport: ({ viewport }) => viewport.range,
      hazardousGoodInfo: ({ salesChannel }) =>
        salesChannel.config.hazardousGoodInfo || false,
    }),
    productTabs() {
      const {
        description = '',
        attributes = [],
        documents = { items: [] },
        setProductsItems = [],
        partsListItems = [],
        gpsrResources,
        hasOeNumbers,
        hasCarTypes,
        articleType,
        infoTexts = [],
      } = this.product || {};

      const hasDescription =
        !!description.length ||
        !!attributes.length ||
        !!(documents && documents.items && documents.items.length) ||
        !!setProductsItems.length ||
        !!partsListItems.length;

      const hasGeneralInfo =
        !!infoTexts &&
        infoTexts.some(({ type }) => type === InfoTextsEnum.GENERAL_INFO);

      const hasTechInfo =
        !!infoTexts &&
        infoTexts.some(({ type }) => type === InfoTextsEnum.TECH_INFO);

      const hasHints = !!articleType;
      const hasGpsr = !!gpsrResources && !!Object.keys(gpsrResources).length;

      const tabs = [];
      if (hasDescription) tabs.push('description');
      if (hasGeneralInfo) tabs.push('generalInfo');
      if (hasTechInfo) tabs.push('techInfo');
      if (hasOeNumbers) tabs.push('spareParts');
      if (hasCarTypes) tabs.push('vehicleTypes');
      if (hasHints) tabs.push('hints');
      if (hasGpsr) tabs.push('gpsr');

      return tabs;
    },
    productBundles() {
      const setProductsItems = this.product.setProductsItems || [];
      const partsListItems = this.product.partsListItems || [];

      return setProductsItems.concat(partsListItems);
    },
    availableTabs() {
      const tabsList = this.productTabs;
      return Object.keys(this.tabs)
        .filter((tab) => tabsList.includes(tab))
        .reduce((filteredTabs, tab) => {
          filteredTabs[tab] = this.tabs[tab];

          return filteredTabs;
        }, {});
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.sectionEls = Array.from(
        this.$el.querySelectorAll('.pdp-tab-section')
      );
      const header =
        this.$refs.tabsHeader || this.$el.querySelector('.tabs-header');
      this.stickyOffset = header ? header.offsetHeight : 0;

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      if (header) {
        header.addEventListener('scroll', this.updateScrollArrows, {
          passive: true,
        });
      }
      window.addEventListener('resize', this.updateScrollArrows, {
        passive: true,
      });
      this.updateScrollArrows();

      if (this.productTabs.length) {
        this.currentTab = this.productTabs[0];
      }

      // Lazy-activate sections once they enter viewport (only once)
      this.initiateSectionsObserver();
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    const header =
      this.$refs.tabsHeader || this.$el.querySelector('.tabs-header');
    if (header) header.removeEventListener('scroll', this.updateScrollArrows);
    window.removeEventListener('resize', this.updateScrollArrows);
    this.destroySectionsObserver();
  },
  methods: {
    isTabActivated(tabKey) {
      return !this.isLazyTab(tabKey) || !!this.activatedTabs[tabKey];
    },
    isLazyTab(tabKey) {
      return tabKey === 'spareParts' || tabKey === 'vehicleTypes';
    },
    onTabClick(tabKey) {
      this.scrollToTab(tabKey);
      this.$nextTick(() => {
        this.scrollHeaderToActive();
        this.updateScrollArrows();
      });
    },
    scrollToTab(tabKey) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(`#${tabKey}`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    handleScroll() {
      if (!this.sectionEls.length) return;

      const viewportTop = window.scrollY + this.stickyOffset + 8;

      let active = this.currentTab;
      for (const el of this.sectionEls) {
        const rect = el.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        if (top <= viewportTop) {
          active = el.id;
        } else {
          break;
        }
      }

      // If none above, default to the first
      if (!active && this.sectionEls[0]) {
        active = this.sectionEls[0].id;
      }

      if (active !== this.currentTab) {
        this.currentTab = active;
        this.$nextTick(() => {
          this.scrollHeaderToActive();
          this.updateScrollArrows();
        });
      }
    },
    updateScrollArrows() {
      const header = this.$refs.tabsHeader;
      if (!header) return;
      const { scrollLeft, scrollWidth, clientWidth } = header;
      this.canScrollLeft = scrollLeft > 2; // allow small tolerance
      this.canScrollRight = scrollLeft + clientWidth < scrollWidth - 2;
    },
    scrollTabs(direction) {
      const header = this.$refs.tabsHeader;
      if (!header) return;
      const delta = Math.round(header.clientWidth * 0.8);
      const left =
        direction === 'left'
          ? header.scrollLeft - delta
          : header.scrollLeft + delta;
      header.scrollTo({ left, behavior: 'smooth' });
      // Defer update to after scroll begins
      requestAnimationFrame(() => this.updateScrollArrows());
    },
    scrollHeaderToActive() {
      const header = this.$refs.tabsHeader;
      if (!header) return;

      const activeEl = header.querySelector('.tab.active');
      if (!activeEl) return;
      if (typeof activeEl.scrollIntoView === 'function') {
        activeEl.scrollIntoView({
          behavior: 'smooth',
          inline: 'nearest',
          block: 'nearest',
        });
      }
    },
    toggleContainer() {
      this.expanded = !this.expanded;
    },
    initiateSectionsObserver() {
      this.sectionsObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = entry.target && entry.target.id;
              if (id) this.$set(this.activatedTabs, id, true);
              this.sectionsObserver &&
                this.sectionsObserver.unobserve(entry.target);
            }
          });
        },
        // Activate slightly before section appears in viewport
        { root: null, rootMargin: '120px 0px', threshold: 0.01 }
      );
      this.sectionEls
        .filter((el) => this.isLazyTab(el.id))
        .forEach((el) => this.sectionsObserver.observe(el));
    },
    destroySectionsObserver() {
      if (
        this.sectionsObserver &&
        typeof this.sectionsObserver.disconnect === 'function'
      ) {
        this.sectionsObserver.disconnect();
        this.sectionsObserver = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import 'mixins';
@import 'variables/breakpoints';

.pdp-tabs {
  .tabs-header-container {
    position: sticky;
    display: flex;
    top: 0;
    z-index: 11;
    background: var(--color-white);
    margin-bottom: var(--space-3);
    border-radius: var(--border-radius-default);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .tabs-header {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    height: 51px;
    border-bottom: 1px solid var(--color-kfzteile24-gray);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    scroll-snap-type: x proximity;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .tab {
      flex: 0 0 auto;
      padding: var(--space-2) var(--space-0--half);
      color: var(--color-nero);
      cursor: pointer;
      height: 100%;
      text-align: center;
      scroll-snap-align: start;
      scroll-snap-stop: always;

      &.active {
        color: var(--color-secondary);
        border-bottom: 2px solid var(--color-secondary);
      }
    }
  }

  .tabs-nav {
    position: absolute;
    appearance: none;
    border: none;
    color: var(--color-nero);
    width: 32px;
    height: 51px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    z-index: 12;

    &.left {
      left: -1px;
      transform: scaleX(1);
      background: linear-gradient(90deg, white, white, #ffffffed, transparent);
    }

    &.right {
      right: -1px;
      transform: scaleX(1);
      background: linear-gradient(90deg, transparent, #ffffffed, white, white);
    }
  }

  .pdp-tab-section {
    scroll-margin-top: 55px; // offset for sticky headers if any

    h3 {
      @include typography-size(var(--font-size-MML), var(--font-size-XL));

      font-weight: 600;
      margin-bottom: var(--space-2);
    }

    .divider {
      margin: var(--space-2) 0;
      height: 1px;
      border: 0;
      background-color: var(--color-rebranding-frenchGray);
    }
  }
}
</style>
